import React, { useState, useEffect } from "react";
import { Truck, MapPin, ArrowRight } from "lucide-react";
import { ASSETS, OUTLETS, I18N } from "../mock";

const Header = ({ lang, setLang }) => (
  <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-[#ececec]">
    <div className="mx-auto max-w-md px-5 h-16 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <img src={ASSETS.foraLogo} alt="FORA" className="h-6 w-auto object-contain" />
        <span className="h-6 w-px bg-[#e2e2e2]" />
        <img src={ASSETS.arenaLogo} alt="Arena Catering" className="h-6 w-auto object-contain" />
      </div>
      <div className="flex items-center rounded-full bg-[#f0f0f0] p-0.5">
        {["en", "pl"].map((l) => (
          <button
            key={l}
            onClick={() => setLang(l)}
            className={`px-3.5 py-1 text-xs font-semibold rounded-full transition-all duration-200 ${
              lang === l
                ? "bg-white text-black shadow-sm"
                : "text-neutral-500 hover:text-neutral-700"
            }`}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  </header>
);

const ZoneBanner = ({ t, count }) => (
  <div className="relative overflow-hidden rounded-2xl h-40 shadow-sm">
    <img
      src={ASSETS.foodTrucksHero}
      alt="Food Trucks"
      className="absolute inset-0 h-full w-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-orange-950/85 via-orange-900/45 to-transparent" />
    <div className="absolute bottom-0 left-0 p-5 flex items-center gap-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500 shadow-lg shadow-orange-900/30">
        <Truck className="h-5 w-5 text-white" strokeWidth={2.2} />
      </div>
      <div>
        <h2 className="text-xl font-bold text-white leading-tight">{t.zone_name}</h2>
      </div>
    </div>
  </div>
);

const OutletCard = ({ outlet, lang, index }) => {
  const desc = lang === "pl" ? outlet.description_pl : outlet.description_en;
  return (
    <a
      href={outlet.menu_url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-4 rounded-2xl bg-white border border-[#ededed] p-3 pr-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] hover:border-[#e0e0e0] animate-rise"
      style={{ animationDelay: `${index * 55}ms` }}
    >
      <div className="h-[68px] w-[68px] flex-shrink-0 overflow-hidden rounded-xl bg-neutral-100">
        <img
          src={outlet.image_url}
          alt={outlet.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-[15px] font-bold text-neutral-900 tracking-tight">
          {outlet.name}
        </h3>
        <p className="mt-0.5 flex items-center gap-1 text-[13px] text-neutral-500">
          <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-[#dd260e]" strokeWidth={2.2} />
          <span className="truncate">{desc}</span>
        </p>
      </div>
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#f2f2f2] text-neutral-500 transition-all duration-300 group-hover:bg-[#dd260e] group-hover:text-white">
        <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
      </div>
    </a>
  );
};

const Footer = ({ t }) => (
  <footer className="mt-8 border-t border-[#ececec] py-6">
    <div className="mx-auto max-w-md flex items-center justify-center gap-3">
      <span className="text-xs text-neutral-400">{t.powered_by}</span>
      <img src={ASSETS.foraLogo} alt="FORA" className="h-4 w-auto object-contain opacity-90" />
      <span className="h-4 w-px bg-[#e2e2e2]" />
      <img
        src={ASSETS.arenaLogo}
        alt="Arena Catering"
        className="h-4 w-auto object-contain opacity-90"
      />
    </div>
  </footer>
);

const ArenaGarden = () => {
  const [lang, setLang] = useState("en");
  const t = I18N[lang];
  const outlets = [...OUTLETS].sort((a, b) => a.sort_order - b.sort_order);

  useEffect(() => {
    document.title = "Arena Garden — Food Trucks | Arena Catering";
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f7f8] font-poppins">
      <div className="mx-auto min-h-screen max-w-md bg-white shadow-[0_0_60px_rgba(0,0,0,0.05)]">
        <Header lang={lang} setLang={setLang} />

        <main className="px-5 pt-6">
          {/* Venue title */}
          <div className="animate-rise">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#dd260e]">
              {t.label}
            </p>
            <h1 className="mt-1.5 text-[32px] font-bold leading-none text-neutral-900">
              {t.venue_title}
            </h1>
          </div>

          {/* Food Trucks banner */}
          <div className="mt-6 animate-rise" style={{ animationDelay: "80ms" }}>
            <ZoneBanner t={t} count={outlets.length} />
          </div>

          {/* Outlet list */}
          <div className="mt-4 space-y-3 pb-2">
            {outlets.map((o, i) => (
              <OutletCard key={o.id} outlet={o} lang={lang} index={i} />
            ))}
          </div>
        </main>

        <Footer t={t} />
      </div>
    </div>
  );
};

export default ArenaGarden;
